import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Send, Loader2, AlertTriangle, MessageSquare, Check, CheckCheck, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { errMsg, retryNotification } from "@/lib/applyApi";
import { useAuth } from "@/lib/auth";
import { useBrand } from "@/lib/brand";

import { timeMT, dayMT } from "@/lib/mountainTime";
const fmt = timeMT;
const day = dayMT;
const POLL_MS = 4000;
// Email outcome for manager messages — only real recorded events (no "queued" without a sender).
const EMAIL = {
  queued: ["Email queued — not yet sent", "text-amber-800"], sending: ["Sending email…", "text-amber-800"],
  sent: ["Email sent (delivery not yet confirmed)", "text-emerald-700"], delivered: ["Email delivered (confirmed by provider)", "text-emerald-800"],
  failed: ["Email failed — seller sees it in their dashboard", "text-red-700"], bounced: ["Email bounced — seller not reached", "text-red-700"],
  complained: ["Marked as spam by recipient", "text-red-700"], cancelled: ["Email cancelled — not sent", "text-stone-600"],
  pending_configuration: ["Email not configured — seller NOT emailed", "text-red-700"],
  test_skipped: ["TEST — not emailed", "text-stone-600"],
  sandbox_skipped: ["Preview sandbox — not emailed", "text-stone-600"],
  approval_covered: ["Not emailed separately (included in the approval email)", "text-stone-600"],
  admin_skipped: ["Not emailed (unticked)", "text-stone-600"],
};

function EmailRetry({ m, onDone }) {
  const [busy, setBusy] = useState(false);
  const nid = (m.notification?.queue_ids || [])[0];
  if (m.notification?.status !== "failed" || !nid) return null;
  const go = async () => {
    setBusy(true);
    try {
      const r = await retryNotification(nid);
      toast[r.status === "sent" ? "success" : "error"](r.status === "sent" ? "Email sent" : `Email ${r.status}: ${r.error || ""}`);
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e, "Could not retry email"));
    } finally {
      setBusy(false);
    }
  };
  return <button type="button" data-testid={`message-email-retry-${m.id}`} disabled={busy} onClick={go} className="ml-1.5 underline text-red-700">{busy ? "Retrying…" : "Retry email"}</button>;
}
const KIND = { info_request: " · Information request", declined: " · Decision", ad_changes_requested: " · Ad changes requested" };
// Automatically generated workflow updates (fixed wording, no personally written text) render as neutral notices.
const SYSTEM_KINDS = new Set(["contract_ready", "contract_signed", "ad_submitted", "ad_approved", "approved", "sale_scheduled", "sale_date_changed"]);
export const isSystemMessage = (m) => m.sender_id === "system" || SYSTEM_KINDS.has(m.kind);

function Status({ m, viewer, onRetry }) {
  if (m.failed) return <button type="button" data-testid={`message-retry-${m.id}`} onClick={() => onRetry(m)} className="inline-flex items-center gap-1 font-semibold underline"><AlertTriangle className="h-3 w-3" />Failed — tap to retry</button>;
  if (m.pending) return <><Loader2 className="h-3 w-3 animate-spin" />Sending</>;
  const read = viewer === "admin" ? m.read_by_seller_at : m.read_by_admin_at;
  return read ? <><CheckCheck className="h-3.5 w-3.5" />Read</> : <><Check className="h-3.5 w-3.5" />Sent</>;
}

function NotifyLine({ m, viewer, onEmailRetried }) {
  const n = EMAIL[m.notification?.status];
  if (!(viewer === "admin" && m.sender_type === "admin" && n)) return null;
  return (
    <div data-testid={`message-notify-${m.id}`} className="mt-1 text-[10px] font-semibold">
      <span className={n[1]}>{n[0]}</span>
      <EmailRetry m={m} onDone={onEmailRetried} />
    </div>
  );
}

function SystemNotice({ m, viewer, onEmailRetried }) {
  return (
    <div data-testid={`message-${m.id}`} data-kind="system" className="flex justify-center px-2 py-1">
      <div className="max-w-[92%] sm:max-w-[80%] text-center">
        <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-stone-400 mb-0.5"><Info className="h-3 w-3" />Update · {fmt(m.created_at)}</div>
        <div className="text-[13px] leading-snug text-stone-600 bg-white border border-stone-200 rounded-xl px-3 py-2 whitespace-pre-wrap break-words">{m.body}</div>
        <NotifyLine m={m} viewer={viewer} onEmailRetried={onEmailRetried} />
      </div>
    </div>
  );
}

const senderLabel = (m, brandName) => (m.sender_type === "admin" ? `${m.sender_name || "Admin"}${brandName ? ` · ${brandName}` : ""}` : m.sender_name || "Seller");

function Bubble({ m, viewer, me, onRetry, onEmailRetried }) {
  const brand = useBrand();
  if (isSystemMessage(m)) return <SystemNotice m={m} viewer={viewer} onEmailRetried={onEmailRetried} />;
  const mine = !!me && m.sender_id === me;
  return (
    <div data-testid={`message-${m.id}`} data-kind={mine ? "outgoing" : "incoming"} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
      {!mine && <div data-testid={`message-sender-${m.id}`} className="text-[11px] text-stone-500 ml-3 mb-0.5">{senderLabel(m, brand?.name)}{KIND[m.kind] || ""}</div>}
      {mine && KIND[m.kind] && <div className="text-[11px] text-stone-500 mr-3 mb-0.5">{KIND[m.kind].slice(3)}</div>}
      <div className={`max-w-[80%] sm:max-w-[70%] rounded-[20px] px-3.5 py-2 text-[15px] sm:text-sm leading-snug whitespace-pre-wrap break-words ${m.failed ? "bg-red-50 border border-red-300 text-red-900 rounded-br-md" : mine ? "bg-[#0A84FF] text-white rounded-br-md" : "bg-[#E9E9EB] text-stone-900 rounded-bl-md"}`}>
        {m.body}
      </div>
      <div data-testid={`message-status-${m.id}`} className={`mt-0.5 flex items-center gap-1 text-[10px] ${mine ? "mr-2" : "ml-3"} ${m.failed ? "text-red-700" : "text-stone-400"}`}>
        {fmt(m.created_at)}
        {mine && <Status m={m} viewer={viewer} onRetry={onRetry} />}
      </div>
      <div className={mine ? "mr-2" : "ml-3"}><NotifyLine m={m} viewer={viewer} onEmailRetried={onEmailRetried} /></div>
    </div>
  );
}

export function MessageThread({ load, send, viewer, title = "Messages", onSent }) {
  const { user } = useAuth();
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const end = useRef(null);
  const list = useRef(null);
  const sending = useRef(0);
  const epoch = useRef(0);
  const stick = useRef(true);

  // Server list is the source of truth (ordered by created_at); local Sending/Failed bubbles stay at the end.
  const apply = useCallback((server) => setMsgs((cur) => [...server, ...(cur || []).filter((x) => x.pending || x.failed)]), []);
  const refresh = useCallback((background) => {
    const visible = document.visibilityState === "visible";
    if (background && !visible) return Promise.resolve();
    const started = epoch.current;
    return load({ background, markRead: visible }).then((r) => { if (!sending.current && started === epoch.current) apply(r); })
      .catch(() => { if (!background) setMsgs((cur) => cur || []); });
  }, [load, apply]);
  useEffect(() => {
    refresh(false);
    const t = setInterval(() => refresh(true), POLL_MS);
    const onVis = () => { if (document.visibilityState === "visible") refresh(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [refresh]);
  // Scroll only the thread's own list, and only when the reader is already at the bottom.
  useEffect(() => { if (list.current && stick.current) list.current.scrollTop = list.current.scrollHeight; }, [msgs]);
  const onScroll = () => { const el = list.current; stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60; };

  const deliver = async (tmp) => {
    sending.current += 1;
    stick.current = true;
    setMsgs((cur) => [...(cur || []).filter((x) => x.id !== tmp.id), { ...tmp, pending: true, failed: false }]);
    try {
      const m = await send(tmp.body);
      setMsgs((cur) => { const rest = (cur || []).filter((x) => x.id !== tmp.id && x.id !== m.id); return [...rest.filter((x) => !x.pending && !x.failed), m, ...rest.filter((x) => x.pending || x.failed)]; });
      onSent?.(m);
    } catch (e) {
      setMsgs((cur) => (cur || []).map((x) => (x.id === tmp.id ? { ...x, pending: false, failed: true } : x)));
      toast.error(errMsg(e, "Could not send message"));
    } finally {
      sending.current -= 1;
      epoch.current += 1; // drop poll responses that started before this send finished
    }
  };
  const submit = async () => {
    const body = text.trim();
    if (!body || busy) return;
    setBusy(true);
    setText("");
    await deliver({ id: `tmp-${Date.now()}`, body, sender_type: viewer, sender_id: user?.id, sender_name: "You", created_at: new Date().toISOString() });
    setBusy(false);
  };

  const unnotified = false;
  return (
    <section id="messages" data-testid="message-thread" className="bg-white border border-stone-200 rounded-xl p-3 sm:p-4 scroll-mt-20">
      <h2 className="font-semibold text-stone-900 flex items-center gap-2 mb-1"><MessageSquare className="h-4 w-4 text-amber-800" /> {title}</h2>
      <p className="text-[11px] text-stone-400 mb-3">{viewer === "admin" ? "Visible to the seller in their dashboard. Updates automatically. Use Internal Notes for private comments." : "Your conversation with the sale team. Updates automatically."}</p>
      {unnotified && (
        <div data-testid="notify-warning" className="mb-3 flex items-start gap-2 text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2">
          <AlertTriangle className="h-4 w-4 shrink-0" /> Email/SMS delivery isn't set up yet, so the seller has NOT been notified of your messages. Contact them directly if it's urgent.
        </div>
      )}
      <div ref={list} onScroll={onScroll} data-testid="message-list" className="space-y-2 max-h-[60vh] sm:max-h-[460px] overflow-y-auto bg-white border border-stone-100 rounded-lg px-2 py-3 min-h-[80px] overscroll-contain">
        {msgs === null ? <Loader2 className="h-4 w-4 animate-spin text-stone-400 m-2" /> :
          msgs.length === 0 ? <p className="text-xs text-stone-400 p-2">No messages yet.</p> :
            msgs.map((m, i) => (
              <div key={m.id}>
                {(i === 0 || day(msgs[i - 1].created_at) !== day(m.created_at)) && (
                  <div data-testid="message-date-separator" className="my-3 text-center text-[11px] font-semibold text-stone-400">{day(m.created_at)}</div>
                )}
                <Bubble m={m} viewer={viewer} me={user?.id} onRetry={deliver} onEmailRetried={() => refresh(false)} />
              </div>
            ))}
        <div ref={end} />
      </div>
      <div className="mt-3 flex items-end gap-2 sticky bottom-0 bg-white pt-1">
        <Textarea data-testid="message-input" rows={1} value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); submit(); } }}
          placeholder={viewer === "admin" ? "Write to the seller…" : "Write a reply…"} className="flex-1 min-h-[44px] max-h-40 text-base sm:text-sm rounded-[22px] px-4 py-2.5 resize-none" />
        <Button data-testid="message-send" onClick={submit} disabled={!text.trim() || busy} aria-label="Send" className="h-11 w-11 sm:w-auto sm:px-5 shrink-0 rounded-full bg-[#0A84FF] hover:bg-[#0071E3] text-white disabled:bg-stone-300">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 sm:mr-1.5" /><span className="hidden sm:inline">Send</span></>}
        </Button>
      </div>
    </section>
  );
}
